import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Carousel } from "../carousel/index";
import aboutBlob from "../../assets/about.gif";
import aboutImg from "../../assets/about-landing.png";
import Loader from "../loader";
import homeData from "../../data/home.json";
import heroData from "../../data/hero.json";
import { Helmet } from "react-helmet";
import { useScrollReveal } from "../../hooks/useScrollReveal";

interface HeroContent {
  heroTitle: string;
  heroSubtitle: string | null;
  slides: {
    title: string;
    description: string;
    image: { asset: { url: string } };
  }[];
}

export default function Home() {
  const [showLoader, setShowLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [heroContent, setHeroContent] = useState<HeroContent>({
    heroTitle: "",
    heroSubtitle: "",
    slides: [],
  });
  const [about, setAbout] = useState({
    heading: "",
    description: "",
  });

  const aboutReveal = useScrollReveal();
  const expertiseReveal = useScrollReveal();

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), 300);

    try {
      setAbout({
        heading: heroData.title,
        description: heroData.description,
      });

      setHeroContent({
        heroTitle: homeData.heroTitle,
        heroSubtitle: homeData.heroSubtitle,
        slides: homeData.slides.map((slide: any, i: number) => ({
          id: i,
          title: slide.title,
          description: slide.caption,
          image: slide.image || "/placeholder.svg",
        })),
      });
    } catch (err) {
      console.error("Error loading local JSON:", err);
    } finally {
      setLoading(false);
      setShowLoader(false);
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, []);

  if (loading && showLoader) {
    return <Loader />;
  }

  // If loading is still true but showLoader is false, render nothing (or minimal UI)
  if (loading && !showLoader) {
    return <div style={{ minHeight: "100vh" }}></div>;
  }

  // Render your content

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>Walls – Forming a Thinking | Architecture & Design Studio</title>
        <meta
          name="description"
          content="Walls - Forming a Thinking. Leading architecture studio creating innovative walls and spaces. Expert in residential, commercial, industrial design. Walls that form thinking, thinking that forms walls."
        />
        <meta name="keywords" content="walls, forming a thinking, walls forming a thinking, architecture, design, residential walls, commercial walls, architectural walls, wall design, thinking walls" />
        <link rel="canonical" href="https://www.wallsconcepts.com" />
        <meta property="og:title" content="Walls – Forming a Thinking | Architecture Studio" />
        <meta property="og:description" content="Walls - Forming a Thinking. Leading architecture studio creating innovative walls and spaces that inspire." />
        <meta property="og:url" content="https://www.wallsconcepts.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.wallsconcepts.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Walls – Forming a Thinking" />
        <meta name="twitter:description" content="Leading architecture studio creating innovative walls and spaces that inspire thinking." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Walls - Forming a Thinking",
            "alternateName": "Walls Concepts",
            "url": "https://www.wallsconcepts.com",
            "logo": "https://www.wallsconcepts.com/logo.jpg",
            "description": "Architecture and design studio specializing in walls, residential, commercial, and institutional projects. Walls forming a thinking.",
            "sameAs": [
              "https://www.instagram.com/wallsconcepts"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "IN"
            },
            "serviceArea": {
              "@type": "Place",
              "name": "India"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Architecture Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Residential Architecture",
                    "description": "Creating living spaces with walls that form thinking"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Design",
                    "description": "Innovative commercial walls and workspaces"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Wall Design",
                    "description": "Specialized wall design and architectural walls"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <main className="flex-1">
        {/* Hero Carousel Section */}
        <section className="w-full ">
          <Carousel items={heroContent.slides} />
        </section>

        {/* About Section */}
        <section className="py-16 md:py-24 bg-muted/50">
          <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
            <div
              ref={aboutReveal.ref}
              className={`grid gap-10 lg:grid-cols-2 lg:gap-16 items-center scroll-reveal ${
                aboutReveal.isVisible ? "is-visible" : ""
              }`}
              style={{ transitionDelay: "100ms" }}
            >
              <div>
                <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                  {about.heading}
                </h2>
                <p className="mt-4 text-muted-foreground md:text-xl">
                  {about.description}
                </p>
                <p className="mt-2 text-muted-foreground">
                  At Walls, we believe in forming a thinking through innovative design. Our walls don't just divide spaces – they create experiences, inspire creativity, and form the foundation of architectural thinking.
                </p>
                <div className="mt-8">
                  <Link
                    to="/about"
                    className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-8 text-base font-semibold text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring bg-[#484331] hover:bg-[#3e3a2b] text-white"
                  >
                    Discover Our Story
                  </Link>
                </div>
              </div>
              <div className="relative aspect-video max-h-[300px] sm:max-h-[400px] overflow-hidden rounded-xl">
                <img
                  src={aboutBlob}
                  alt="Animated background"
                  className="absolute inset-0 w-full h-full object-cover z-0"
                />
                <img
                  src={aboutImg}
                  alt="Architectural studio"
                  className="relative w-full h-full object-contain z-10"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-16 md:py-24">
          <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
            <div className="flex flex-col items-center text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                Our Expertise in Walls & Design
              </h2>
              <p className="mt-4 max-w-[700px] text-muted-foreground md:text-xl">
                We specialize in creating walls that form thinking – from residential walls to commercial spaces, each project reflects our philosophy of walls forming a thinking approach to architecture.
              </p>
            </div>
            <div
              ref={expertiseReveal.ref}
              className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 scroll-reveal ${
                expertiseReveal.isVisible ? "is-visible" : ""
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              {expertiseAreas.map((area, index) => (
                <div
                  key={index}
                  className="group flex flex-col items-center text-center p-6 rounded-lg border bg-card shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="h-12 w-12 flex items-center justify-center rounded-full bg-primary/10 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#484331]">
                    {area.icon === "Home" && (
                      <HomeIcon className="h-6 w-6 text-[#484331] group-hover:text-white transition-colors duration-300" />
                    )}
                    {area.icon === "Building2" && (
                      <Building2Icon className="h-6 w-6 text-[#484331] group-hover:text-white transition-colors duration-300" />
                    )}
                    {area.icon === "Landmark" && (
                      <LandmarkIcon className="h-6 w-6 text-[#484331] group-hover:text-white transition-colors duration-300" />
                    )}
                    {area.icon === "Sofa" && (
                      <SofaIcon className="h-6 w-6 text-[#484331] group-hover:text-white transition-colors duration-300" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold">{area.title}</h3>
                  <p className="mt-2 text-muted-foreground">
                    {area.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <Link
                to="/portfolio"
                className="inline-flex h-12 items-center justify-center rounded-md border border-input bg-[#484331] hover:bg-[#3e3a2b] text-white px-8 text-base font-semibold shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Explore Our Work
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

// Expertise areas array
const expertiseAreas = [
  {
    title: "Residential Architecture",
    description:
      "Creating living spaces that blend functionality with aesthetic appeal",
    icon: "Home",
  },
  {
    title: "Commercial Design",
    description:
      "Innovative workspaces that enhance productivity and well-being",
    icon: "Building2",
  },
  {
    title: "Institutional Planning",
    description: "Purpose-driven spaces for empowered learning and governance",
    icon: "Landmark",
  },
  {
    title: "Interior Design",
    description:
      "Thoughtful interior spaces that complement architectural vision",
    icon: "Sofa",
  },
];

// Icons as named exports
export function HomeIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export function Building2Icon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 22V2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v20" />
      <path d="M6 12H4" />
      <path d="M6 17H4" />
      <path d="M6 7H4" />
      <path d="M18 22V6a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v16" />
      <path d="M18 17h-4" />
      <path d="M18 12h-4" />
      <path d="M23 22V10a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v12" />
      <path d="M23 17h-4" />
    </svg>
  );
}

export function LandmarkIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" x2="21" y1="22" y2="22" />
      <line x1="6" x2="6" y1="18" y2="11" />
      <line x1="10" x2="10" y1="18" y2="11" />
      <line x1="14" x2="14" y1="18" y2="11" />
      <line x1="18" x2="18" y1="18" y2="11" />
      <polygon points="12 2 20 7 4 7" />
    </svg>
  );
}

export function SofaIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3" />
      <path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z" />
      <path d="M4 18v2" />
      <path d="M20 18v2" />
      <path d="M12 4v9" />
    </svg>
  );
}
